import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  MessageCircle,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { BUSINESS } from "../constants";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Endpoint für das Kontaktformular.
// - In der Vorschau (Emergent) per default das FastAPI-Backend.
// - Im Produktions-Build (Alfahosting): REACT_APP_CONTACT_ENDPOINT=/contact.php
//   in .env.production setzt eine relative URL zur PHP-Mailer-Datei.
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || `${API}/contact`;

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "", // Honeypot – muss leer bleiben
  });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(CONTACT_ENDPOINT, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || null,
        message: form.message.trim(),
        website: form.website, // Honeypot
      });
      setStatus("success");
      toast.success(t.contact.form_success);
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("error");
      toast.error(t.contact.form_error);
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  const isSending = status === "sending";

  return (
    <section
      id="kontakt"
      className="relative py-20 md:py-32 px-5 md:px-8 lg:px-12 bg-[#05080F]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 md:mb-14 max-w-3xl">
          <p className="font-script text-[#E63946] text-base md:text-xl mb-3">
            — {t.contact.eyebrow}
          </p>
          <h2
            className="font-heading font-bold uppercase tracking-tight text-3xl sm:text-5xl md:text-6xl text-white mb-4 md:mb-5 leading-[0.95]"
            data-testid="contact-title"
          >
            {t.contact.title}
          </h2>
          <p className="text-white/60 text-sm sm:text-base md:text-lg">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-10 md:gap-14">
          <div className="md:col-span-3 space-y-8 md:space-y-10">
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-heading uppercase tracking-wider text-white/60 mb-2">
                    {t.contact.form_name}
                  </label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    minLength={1}
                    autoComplete="name"
                    className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-[#E63946] focus-visible:border-[#E63946]"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-heading uppercase tracking-wider text-white/60 mb-2">
                    {t.contact.form_email}
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-[#E63946] focus-visible:border-[#E63946]"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-heading uppercase tracking-wider text-white/60 mb-2">
                  {t.contact.form_phone}
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-[#E63946] focus-visible:border-[#E63946]"
                  data-testid="contact-input-phone"
                />
              </div>

              <div>
                <label className="block text-xs font-heading uppercase tracking-wider text-white/60 mb-2">
                  {t.contact.form_message}
                </label>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  minLength={1}
                  rows={5}
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E63946] focus-visible:border-[#E63946] resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              {/* Honeypot: für Menschen unsichtbar, Bots füllen es aus */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={handleChange}
                  />
                </label>
              </div>

              <p className="text-xs text-white/45 leading-relaxed" data-testid="contact-privacy-hint">
                {t.contact.form_privacy}{" "}
                <Link
                  to="/datenschutz"
                  className="text-white/70 hover:text-[#E63946] underline underline-offset-2"
                >
                  {t.contact.form_privacy_link}
                </Link>
                .
              </p>

              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 pt-1">
                <button
                  type="submit"
                  disabled={isSending}
                  className="inline-flex items-center justify-center gap-3 bg-[#E63946] hover:bg-[#D92D39] disabled:opacity-70 disabled:cursor-not-allowed text-white font-heading uppercase tracking-wider px-6 py-3.5 rounded-full transition-all duration-300 hover:shadow-[0_0_30px_rgba(230,57,70,0.35)]"
                  data-testid="contact-submit-btn"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t.contact.form_sending}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>{t.contact.form_submit}</span>
                    </>
                  )}
                </button>

                <a
                  href={BUSINESS.whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 text-white font-heading uppercase tracking-wider px-6 py-3.5 rounded-full transition-all"
                  data-testid="contact-whatsapp-link"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp</span>
                </a>

                {status === "success" && (
                  <span className="inline-flex items-center gap-2 text-sm text-emerald-400" data-testid="contact-success">
                    <CheckCircle2 className="w-4 h-4" />
                    {t.contact.form_success}
                  </span>
                )}
                {status === "error" && (
                  <span className="inline-flex items-center gap-2 text-sm text-[#E63946]" data-testid="contact-error">
                    <AlertCircle className="w-4 h-4" />
                    {t.contact.form_error}
                  </span>
                )}
              </div>
            </form>

            <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
              <InfoCard icon={MapPin} title={t.contact.address_title} testId="info-address">
                <a
                  href={BUSINESS.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/80 hover:text-[#E63946] transition-colors"
                >
                  {t.contact.address}
                </a>
              </InfoCard>
              <InfoCard icon={Phone} title={t.contact.phone_title} testId="info-phone">
                <a
                  href={BUSINESS.phoneHref}
                  className="text-white/80 hover:text-[#E63946] transition-colors"
                >
                  {t.contact.phone}
                </a>
              </InfoCard>
              <InfoCard icon={Mail} title={t.contact.email_title} testId="info-email">
                <a
                  href={BUSINESS.emailHref}
                  className="text-white/80 hover:text-[#E63946] transition-colors break-all"
                >
                  {t.contact.email}
                </a>
              </InfoCard>
              <InfoCard icon={Clock} title={t.contact.hours_title} testId="info-hours">
                <ul className="space-y-1.5 text-white/75 text-sm">
                  {t.contact.hours.map((h, i) => (
                    <li key={i} className="flex justify-between gap-4">
                      <span className="font-heading uppercase tracking-wide text-xs text-white/60">
                        {h.day}
                      </span>
                      <span>{h.time}</span>
                    </li>
                  ))}
                </ul>
              </InfoCard>
            </div>
          </div>

          <div className="md:col-span-2">
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] md:aspect-auto md:h-full min-h-[360px] md:min-h-[500px]"
              data-testid="map-embed"
            >
              <iframe
                title="YOUR CUT Hamburg Karte"
                src={BUSINESS.mapsEmbed}
                className="w-full h-full"
                style={{ border: 0, filter: "grayscale(0.4) contrast(1.1)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const InfoCard = ({ icon: Icon, title, children, testId }) => (
  <div
    className="p-4 md:p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#E63946]/40 transition-colors"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#E63946]" />
      <h3 className="font-heading uppercase tracking-wide text-xs text-white/60">
        {title}
      </h3>
    </div>
    <div className="text-white/80 text-sm sm:text-base">{children}</div>
  </div>
);
